
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { ScriptDisplay } from "./ScriptDisplay";
import { PixelScriptMetadata } from "../../UpdatePixelScript";
import { ScriptFormatter } from "./ScriptFormatter";
import { Copy } from "lucide-react";
import { toast } from "sonner";

interface AdvancedOptionsProps {
  pixelId: string;
  scriptMetadata: PixelScriptMetadata | null;
}

export function AdvancedOptions({ pixelId, scriptMetadata }: AdvancedOptionsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const debugScript = ScriptFormatter.getDebugTrackingScript(pixelId, scriptMetadata);
  const disabledScript = ScriptFormatter.getDisabledTrackingScript(pixelId, scriptMetadata);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} script copied to clipboard`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        className="text-xs mt-2"
        onClick={() => setShowAdvanced(!showAdvanced)}
      >
        {showAdvanced ? "Hide Advanced Options" : "Show Advanced Options"}
      </Button>
      
      {showAdvanced && (
        <div className="p-3 mt-2 bg-slate-50 border border-slate-200 rounded-md">
          <h4 className="text-sm font-medium">Debug Mode</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Use this script when testing implementation. It disables domain validation and integrity checks.
          </p>
          
          <ScriptDisplay script={debugScript} />
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2"
            onClick={() => copyToClipboard(debugScript, "Debug")}
          >
            <Copy className="w-3 h-3 mr-1" />
            Copy Debug Script
          </Button>
          
          <div className="mt-4 bg-amber-50 p-3 rounded-md border border-amber-200">
            <h5 className="text-xs font-medium text-amber-800">Troubleshooting Tips</h5>
            <ul className="text-xs text-amber-700 mt-1 list-disc pl-5 space-y-1">
              <li>If you see 401 Unauthorized errors, make sure you're using the debug script for testing</li>
              <li>Always use the crossorigin attribute when adding the script to your site</li>
              <li>Clear your browser cache if you're still seeing old errors</li>
              <li>Use the browser dev tools Network tab to check for script loading issues</li>
            </ul>
          </div>
          
          <div className="mt-4">
            <h4 className="text-sm font-medium">Privacy Options</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Disable tracking by using the following script:
            </p>
            
            <div className="mt-2">
              <ScriptDisplay script={disabledScript} />
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => copyToClipboard(disabledScript, "Disabled")}
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy Disabled Script
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
